import { FC, useMemo, useState } from "react";
import { Check } from "lucide-react";
import { SearchablePopover, cn } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { useDisabledJobTypes } from "~/shared/hooks/useDisabledJobTypes";
import {
  filterJobTypeOptions,
  useJobTypeOptions,
  type JobTypeOption,
} from "~/shared/hooks/useJobTypeOptions";
import type { JobTypeTariff } from "~/shared/lib/tariff-rate";

const PickerRow: FC<{
  checked: boolean;
  onClick: () => void;
  label: string;
  category?: string;
}> = ({ checked, onClick, label, category }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }}
    className="flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-left last:border-b-0 hover:bg-blue-50"
  >
    <span
      className={cn(
        "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
        checked ? "border-blue-600 bg-blue-600" : "border-slate-300 bg-slate-200",
      )}
    >
      {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
    </span>
    <span className="min-w-0 flex-1 truncate text-sm text-text-body">{label}</span>
    {category && <span className="shrink-0 text-xs font-medium text-blue-600">{category}</span>}
  </button>
);

/**
 * «Выбрать типы задач»: a multiselect of job types. Toggling a type adds or
 * removes its tariff row, and the panel stays open for the next one. The rows
 * themselves stay with each form, which lays them out its own way.
 */
export const JobTypePicker: FC<{
  tariffs: JobTypeTariff[];
  onToggle: (tariffKey: string) => void;
  inputId: string;
  disabled?: boolean;
}> = ({ tariffs, onToggle, inputId, disabled }) => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { options } = useJobTypeOptions(t);
  const { types: disabledTypes } = useDisabledJobTypes();
  const visibleOptions = useMemo(
    () =>
      filterJobTypeOptions(
        options.filter((option) => option.category && !disabledTypes.has(option.type)),
        searchQuery,
      ),
    [options, disabledTypes, searchQuery],
  );
  const isSelected = (option: JobTypeOption) =>
    tariffs.some((tariff) => tariff.type === option.tariffKey);
  const allSelected = visibleOptions.length > 0 && visibleOptions.every(isSelected);
  // Selects every visible type, or clears them all once each one is already picked
  const toggleAll = () =>
    visibleOptions
      .filter((option) => isSelected(option) === allSelected)
      .forEach((option) => onToggle(option.tariffKey));

  return (
    <SearchablePopover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearchQuery("");
      }}
      trigger={
        <button
          type="button"
          disabled={disabled}
          className="text-sm font-medium text-blue-600 hover:text-blue-700 focus:outline-none disabled:text-slate-400"
        >
          {t(Namespace.Customers, "onboarding.contract.selectJobTypes")}
        </button>
      }
      searchValue={searchQuery}
      onSearchChange={setSearchQuery}
      inputId={inputId}
      placeholder={t(Namespace.Customers, "modal.addContract.searchJobType")}
      contentClassName="w-80"
      contentWidth="custom"
      contentProps={{ align: "end" }}
    >
      {visibleOptions.length > 0 ? (
        <>
          <PickerRow
            checked={allSelected}
            onClick={toggleAll}
            label={t(Namespace.Customers, "modal.addContract.selectAllJobTypes")}
          />
          {visibleOptions.map((option) => (
            <PickerRow
              key={option.type}
              checked={isSelected(option)}
              onClick={() => onToggle(option.tariffKey)}
              label={option.label}
              category={option.category}
            />
          ))}
        </>
      ) : (
        <div className="px-3 py-2 text-sm text-icon-secondary">
          {searchQuery
            ? t(Namespace.Customers, "modal.addContract.jobTypesNotFound")
            : t(Namespace.Customers, "modal.addContract.allJobTypesAdded")}
        </div>
      )}
    </SearchablePopover>
  );
};
